<template>
  <section class="bs-step" aria-labelledby="bs-heading" data-testid="branding-step">
    <div class="bs-heading">
      <h2 id="bs-heading" class="bs-title" data-testid="bs-title">{{ title }}</h2>
      <p v-if="subtitle" class="bs-subtitle" data-testid="bs-subtitle">{{ subtitle }}</p>
    </div>

    <div class="bs-content">
      <div class="bs-logo">
        <div class="bs-logo-header">
          <span class="bs-logo-label">Company logo</span>
          <div v-if="previewUrl" class="bs-logo-actions">
            <StandardButton
              variant="text-primary"
              size="small"
              data-testid="bs-replace-logo"
              @click="openFilePicker"
            >
              Replace
            </StandardButton>
            <StandardButton
              variant="text-primary"
              size="small"
              data-testid="bs-remove-logo"
              @click="removeLogo"
            >
              Remove
            </StandardButton>
          </div>
        </div>

        <div
          class="bs-drop"
          role="button"
          tabindex="0"
          aria-label="Upload company logo"
          data-testid="bs-drop"
          @click="openFilePicker"
          @keydown.enter.prevent="openFilePicker"
          @keydown.space.prevent="openFilePicker"
        >
          <img
            v-if="previewUrl"
            class="bs-drop-img"
            :src="previewUrl"
            alt="Company logo preview"
            data-testid="bs-drop-img"
          />
          <span v-else class="bs-upload" data-testid="bs-upload">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M12 16V8M8 12L12 8L16 12M4 20H20"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Upload logo
          </span>
        </div>
        <input
          ref="fileInput"
          type="file"
          :accept="ACCEPT_ATTRIBUTE"
          class="bs-file-input"
          tabindex="-1"
          aria-hidden="true"
          data-testid="bs-file-input"
          @change="handleFileChange"
        />
        <p class="bs-hint" data-testid="bs-hint">PNG, JPG or SVG, up to {{ MAX_LOGO_MB }} MB.</p>
        <p v-if="errorMessage" class="bs-error" role="alert" data-testid="bs-error">
          {{ errorMessage }}
        </p>
      </div>

      <div class="bs-preview" data-testid="bs-preview">
        <span class="bs-preview-title">Preview</span>
        <OrganizationBrand v-if="previewUrl" :logo="previewUrl" :account-name="accountName" />
        <span v-else class="bs-preview-empty" data-testid="bs-preview-empty">
          Your logo will appear here.
        </span>
      </div>
    </div>

    <div class="bs-spacer" aria-hidden="true"></div>

    <div class="bs-actions">
      <StandardButton
        variant="primary"
        :disabled="!canContinue"
        data-testid="bs-continue"
        @click="handleContinue"
      >
        {{ buttonLabel }}
      </StandardButton>
      <StandardButton variant="text-secondary" data-testid="bs-skip" @click="emit('skip')">
        Skip for now
      </StandardButton>
      <StandardButton
        variant="text-secondary"
        data-testid="bs-do-not-ask"
        @click="emit('do-not-ask')"
      >
        Do not ask again
      </StandardButton>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from "vue";
import OrganizationBrand from "@components/OrganizationBrand/OrganizationBrand.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";

/**
 * ER-12246 — BrandingStep (MDP-621).
 *
 * "Add your company logo" (page key `branding`). The chosen file is previewed as
 * is (no cropping, Eric Kish 2026-10-16) and held until Continue, which hands the
 * file to the flow controller to upload. Skip for now saves nothing; Do not ask
 * again hides the page for good. Presentational — it validates the file and emits.
 */
defineProps({
  title: { type: String, default: "Add your company logo" },
  subtitle: {
    type: String,
    default:
      "Your logo appears on the welcome page and across exIQtive so your team recognizes your organization.",
  },
  accountName: { type: String, default: "" },
  buttonLabel: { type: String, default: "Continue" },
});

const emit = defineEmits(["continue", "skip", "do-not-ask"]);

// Matches the backend logo cap (5 MB) and the design copy; hint and error text derive from this constant.
const MAX_LOGO_MB = 5;
const MAX_LOGO_BYTES = MAX_LOGO_MB * 1024 * 1024;
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/svg+xml"];
const ACCEPT_ATTRIBUTE = ACCEPTED_TYPES.join(",");
const INVALID_FILE_MESSAGE = `This file can’t be used. Upload a PNG, JPG or SVG file up to ${MAX_LOGO_MB} MB.`;

const fileInput = ref(null);
const file = ref(null);
const previewUrl = ref("");
const errorMessage = ref("");

const canContinue = computed(() => Boolean(file.value));

function releasePreview() {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
  }
  previewUrl.value = "";
}

function openFilePicker() {
  fileInput.value?.click();
}

function isUsableLogo(candidate) {
  return (
    ACCEPTED_TYPES.includes(String(candidate.type).toLowerCase()) &&
    candidate.size > 0 &&
    candidate.size <= MAX_LOGO_BYTES
  );
}

function handleFileChange(event) {
  const picked = event?.target?.files?.[0];
  if (event?.target) event.target.value = "";
  if (!picked) return;

  if (!isUsableLogo(picked)) {
    errorMessage.value = INVALID_FILE_MESSAGE;
    return;
  }

  errorMessage.value = "";
  releasePreview();
  file.value = picked;
  previewUrl.value = URL.createObjectURL(picked);
}

function removeLogo() {
  releasePreview();
  file.value = null;
  errorMessage.value = "";
}

function handleContinue() {
  if (!canContinue.value) return;
  emit("continue", { file: file.value });
}

onBeforeUnmount(releasePreview);
</script>

<style scoped>
.bs-step {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  height: 100%;
  padding: 0;
  background: #ffffff;
  color: #101828;
  font-family: Inter, sans-serif;
}

.bs-heading {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bs-title {
  margin: 0;
  color: #101828;
  font-size: 28px;
  font-weight: 500;
  line-height: 34px;
}

.bs-subtitle {
  margin: 0;
  color: #475467;
  font-size: 16px;
  line-height: 24px;
}

.bs-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.bs-logo-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.bs-logo-label {
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.bs-logo-actions {
  display: flex;
  gap: 12px;
}

.bs-drop:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.bs-drop {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  min-height: 96px;
  background: #f9fafb;
  border: 1px dashed #d0d5dd;
  border-radius: 8px;
  cursor: pointer;
}

.bs-drop-img {
  width: 100%;
  height: 160px;
  object-fit: contain;
}

.bs-upload {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #475467;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.bs-file-input {
  display: none;
}

.bs-hint {
  margin: 8px 0 0;
  color: #667085;
  font-size: 14px;
  line-height: 20px;
}

.bs-error {
  margin: 8px 0 0;
  color: #b42318;
  font-size: 14px;
  line-height: 20px;
}

.bs-preview {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 8px;
}

.bs-preview-title {
  color: #667085;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
}

.bs-preview-empty {
  color: #667085;
  font-size: 14px;
  line-height: 20px;
}

.bs-spacer {
  flex: 1 1 auto;
}

.bs-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
